import React from 'react';
import {
  ArrowRightCircle,
  Ban,
  Download,
  GitBranch,
  History,
  Kanban,
  List,
  Pause,
  Play,
  MessageSquarePlus,
  RotateCw,
  Square,
} from 'lucide-react';
import { EnterpriseRibbon } from '../../../components/layout/EnterpriseRibbon';
import { RibbonDesktopButton } from '../../../components/ribbon/DesktopRibbonControls';

/* ─── Props ───────────────────────────────────────────────── */

interface WorkflowsRibbonProps {
  // Gestão
  onNew?: () => void;
  canNew?: boolean;
  newTooltip?: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  onExport?: () => void;
  canExport?: boolean;
  // Controle de Fluxo
  pauseLabel?: string;
  onPause?: () => void;
  canPause?: boolean;
  onCancel?: () => void;
  canCancel?: boolean;
  onAdvance?: () => void;
  canAdvance?: boolean;
  onBlock?: () => void;
  canBlock?: boolean;
  // Compat (não renderizados, mantidos para não quebrar callers)
  instanceBusy?: boolean;
  onStart?: () => void;
  canStart?: boolean;
  onComplete?: () => void;
  canComplete?: boolean;
  onReprocess?: () => void;
  canReprocess?: boolean;
  // Visões
  onViewInstances?: () => void;
  instancesActive?: boolean;
  onViewKanban?: () => void;
  kanbanActive?: boolean;
  onViewHistory?: () => void;
  // Filtros (slot)
  filtersSlot?: React.ReactNode;
}

/* ─── Component ───────────────────────────────────────────── */

export function WorkflowsRibbon({
  onNew,
  canNew = false,
  newTooltip,
  onRefresh,
  isRefreshing = false,
  onExport,
  canExport = false,
  pauseLabel = 'Pausar',
  onPause,
  canPause = false,
  onCancel,
  canCancel = false,
  onAdvance,
  canAdvance = false,
  onBlock,
  canBlock = false,
  instanceBusy = false,
  onViewInstances,
  instancesActive = false,
  onViewKanban,
  kanbanActive = false,
  onViewHistory,
  filtersSlot,
}: WorkflowsRibbonProps) {
  const PauseIcon = pauseLabel === 'Retomar' ? Play : Pause;

  return (
    <EnterpriseRibbon moduleId="workflow">

      {/* Grupo 1 — Gestão */}
      <EnterpriseRibbon.Group label="Gestão">
        <RibbonDesktopButton
          icon={<MessageSquarePlus strokeWidth={1.5} />}
          label="Workflow"
          disabled={!canNew || instanceBusy}
          onClick={onNew}
          title={newTooltip}
        />
        <RibbonDesktopButton
          icon={<RotateCw strokeWidth={1.5} />}
          label="Atualizar"
          disabled={isRefreshing}
          onClick={onRefresh}
        />
        <RibbonDesktopButton
          icon={<Download strokeWidth={1.5} />}
          label="Exportar"
          disabled={!canExport}
          onClick={onExport}
          title={canExport ? 'Exportar lista como CSV' : 'Nenhum dado para exportar'}
        />
      </EnterpriseRibbon.Group>

      {/* Grupo 2 — Controle de Fluxo */}
      <EnterpriseRibbon.Group label="Controle de Fluxo">
        <RibbonDesktopButton
          icon={<PauseIcon strokeWidth={1.5} />}
          label={pauseLabel}
          disabled={!canPause || instanceBusy}
          onClick={onPause}
        />
        <RibbonDesktopButton
          icon={<Square strokeWidth={1.5} />}
          label="Finalizar"
          disabled={!canCancel || instanceBusy}
          onClick={onCancel}
        />
        <RibbonDesktopButton
          icon={<ArrowRightCircle strokeWidth={1.5} />}
          label="Avançar"
          disabled={!canAdvance || instanceBusy}
          onClick={onAdvance}
        />
        <RibbonDesktopButton
          icon={<Ban strokeWidth={1.5} />}
          label="Bloquear"
          disabled={!canBlock || instanceBusy}
          onClick={onBlock}
          title={canBlock ? 'Bloquear etapa atual' : 'Bloqueio indisponível'}
        />
      </EnterpriseRibbon.Group>

      {/* Grupo 3 — Visões */}
      <EnterpriseRibbon.Group label="Visões">
        <RibbonDesktopButton
          icon={<List strokeWidth={1.5} />}
          label="Instâncias"
          active={instancesActive}
          onClick={onViewInstances}
        />
        <RibbonDesktopButton
          icon={<Kanban strokeWidth={1.5} />}
          label="Kanban"
          active={kanbanActive}
          onClick={onViewKanban}
        />
        <RibbonDesktopButton
          icon={<History strokeWidth={1.5} />}
          label="Histórico"
          disabled={!onViewHistory}
          onClick={onViewHistory}
          title={onViewHistory ? 'Ver histórico' : 'Em breve'}
        />
      </EnterpriseRibbon.Group>

      {/* Filtros — extrema direita */}
      {filtersSlot && (
        <EnterpriseRibbon.Filters>
          {filtersSlot}
        </EnterpriseRibbon.Filters>
      )}

    </EnterpriseRibbon>
  );
}

export default WorkflowsRibbon;
